import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Pencil, Check, X, Sticker } from 'lucide-react';

const StickerRow = ({ sticker, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(sticker.name);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return toast.error('Sticker name is required');
    try {
      await axios.put(`${API}/categories/${sticker.id}`, { name: trimmed });
      toast.success('Sticker renamed');
      setEditing(false);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to rename sticker'); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete sticker "${sticker.name}"? It will also be removed from every recipe that uses it.`)) return;
    try {
      await axios.delete(`${API}/categories/${sticker.id}`);
      toast.success('Sticker deleted');
      onChanged();
    } catch { toast.error('Failed to delete sticker'); }
  };

  return (
    <div className="flex items-center justify-between gap-2 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow" data-testid={`sticker-${sticker.id}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <Sticker className="w-4 h-4 text-purple-600 shrink-0" />
        {editing ? (
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-sm" autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
            data-testid={`sticker-rename-input-${sticker.id}`} />
        ) : (
          <span className="font-medium text-sm truncate" data-testid={`sticker-name-${sticker.id}`}>{sticker.name}</span>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {editing ? (
          <>
            <button onClick={save} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg" data-testid={`sticker-rename-save-${sticker.id}`}><Check className="w-3.5 h-3.5" /></button>
            <button onClick={() => { setEditing(false); setName(sticker.name); }} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg" data-testid={`sticker-rename-cancel-${sticker.id}`}><X className="w-3.5 h-3.5" /></button>
          </>
        ) : (
          <button onClick={() => setEditing(true)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg" data-testid={`sticker-edit-${sticker.id}`}><Pencil className="w-3.5 h-3.5" /></button>
        )}
        <button onClick={remove} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`sticker-delete-${sticker.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
};

export const StickerTypesPanel = ({ stickers, onChanged }) => {
  const [newName, setNewName] = useState('');

  const add = async (e) => {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (stickers.some(s => s.name.trim().toLowerCase() === trimmed.toLowerCase())) return toast.error('That sticker already exists');
    try {
      await axios.post(`${API}/categories`, null, { params: { name: trimmed, type: 'sticker_type' } });
      toast.success('Sticker added');
      setNewName('');
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add sticker'); }
  };

  return (
    <div data-testid="sticker-types-panel">
      <p className="text-sm text-gray-500 mb-4">Stickers that can be assigned to Final Recipes (up to 3 per recipe). They show on production sheets, packing sheets and Client Packing.</p>
      <form onSubmit={add} className="flex gap-2 mb-4 max-w-md">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g., Vegan, Contains Nuts" data-testid="new-sticker-input" />
        <Button type="submit" className="btn-primary" disabled={!newName.trim()} data-testid="add-sticker-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Sticker
        </Button>
      </form>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {stickers.map(s => <StickerRow key={s.id} sticker={s} onChanged={onChanged} />)}
        {stickers.length === 0 && <div className="col-span-full text-center py-8 text-gray-500" data-testid="no-stickers-msg">No stickers yet. Add your first sticker above.</div>}
      </div>
    </div>
  );
};
